import {
  useSuspenseQuery,
  type UseSuspenseQueryResult,
  type UseSuspenseQueryOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { getWfAuthnClient, setAuthCookies, useAxios, isWfAuthnMockEnabled } from "auth-sdk";
import { BaseSchemas } from "api-types";
import { isAxiosError } from "axios";
import type { AxiosError, AxiosResponse } from "axios";
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { StaffAppConfigSchemaOut } from "staff-v2-types";
import { CUSTOMER_UUID_QUERY_KEY } from "shared-utils";

export const STAFF_APP_CONFIG_QUERY_KEY = "staff-app-config";
export const CUSTOMER_APP_CONFIG_QUERY_KEY = "customer-app-config";

// When a user signed up with username/password tries to log in with Google (or vice versa), the backend can't resolve
// their user and responds to the app-config request with a 400. On the `/authn-wf/email-constraint` page we still need
// *an* app config so the page can render rather than tripping the error boundary onto a generic error page. There is no
// authenticated user in this state, so this mirrors the unauthenticated/public config the backend would otherwise send.
const EMAIL_CONSTRAINT_FALLBACK_APP_CONFIG: BaseSchemas.CustomerAppConfig = {
  analyticsAttributes: {
    companyPublicId: null,
    createdAt: null,
    hubspotContactId: null,
    lastLogin: null,
    userId: null,
    userUuid: null,
  },
  customerDomain: "",
  customerName: "",
  featureFlags: {},
  googleClientId: "",
  homePath: "",
  logoPath: "",
  logoutPath: "",
  navItems: [],
  userEmail: "",
  userName: "",
  userPermissions: [],
};

export const useStaffAppConfig = (): UseSuspenseQueryResult<StaffAppConfigSchemaOut> => {
  const headers = { "x-current-location": window.location.pathname };
  const axiosClient = useAxios();
  return useSuspenseQuery<AxiosResponse<StaffAppConfigSchemaOut>, AxiosError, StaffAppConfigSchemaOut>({
    queryKey: [STAFF_APP_CONFIG_QUERY_KEY],
    queryFn: async () => {
      if (!isWfAuthnMockEnabled()) {
        // HACK: Blast in access token cookies incase they became expired.
        // Since this gets called almost immediately and suspends everything else,
        // it's a decent place to inject them.
        const client = getWfAuthnClient();
        let accessToken = await client.getAccessToken();
        if (accessToken === undefined) {
          accessToken = await client.getAccessToken({ refresh: true });
          if (accessToken === undefined) {
            const next = window.location.pathname + window.location.search;
            window.location.href = "/authn/login-staff?next=" + encodeURIComponent(next);
            throw new Error("Can't get access token, redirecting to login");
          }
        }
        setAuthCookies({ accessToken: accessToken.jwt });
      }
      return axiosClient.get<StaffAppConfigSchemaOut>("/api/staff/v2/api/app-config", { headers });
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ data }) => data,
  });
};
export const useCustomerAppConfig = (
  options: Omit<
    UseSuspenseQueryOptions<AxiosResponse<BaseSchemas.CustomerAppConfig>, AxiosError, BaseSchemas.CustomerAppConfig>,
    "queryKey" | "queryFn" | "select"
  > = {}
): UseSuspenseQueryResult<BaseSchemas.CustomerAppConfig> => {
  const { customerDomain } = useParams();
  const queryClient = useQueryClient();
  const axiosClient = useAxios();
  const isAuthPath = location.pathname.includes("authn-wf");
  const path = customerDomain ? `/api/${customerDomain}/app-config` : "/api/app-config";
  return useSuspenseQuery<AxiosResponse<BaseSchemas.CustomerAppConfig>, AxiosError, BaseSchemas.CustomerAppConfig>({
    queryKey: [CUSTOMER_APP_CONFIG_QUERY_KEY, customerDomain ? customerDomain : "public"],
    queryFn: async () => {
      try {
        const response = await axiosClient.get<BaseSchemas.CustomerAppConfig>(path);

        // Cache the customerUuid for the duration of the session
        if (response.data.customerUuid) {
          queryClient.setQueryDefaults([CUSTOMER_UUID_QUERY_KEY], {
            staleTime: Infinity,
            gcTime: Infinity,
          });
          queryClient.setQueryData([CUSTOMER_UUID_QUERY_KEY], response.data.customerUuid);
        }

        return response;
      } catch (e: unknown) {
        if (isAxiosError(e)) {
          // If we get a 404, it might be because the customer domain doesn't exist. In that case, we can fallback to the public app config.
          if (e.status === 404) {
            const response = await axiosClient.get<BaseSchemas.CustomerAppConfig>("/api/app-config");
            return response;
          }
          // Unauthenticated. The backend returns a 401 here rather than an HTML redirect to login (which we couldn't
          // follow, and whose `next` would point at this API URL). Send the user to login and return them to the
          // *page* they were on once they re-authenticate, then suspend until the browser navigates away so we don't
          // flash the error boundary.
          if (e.status === 401) {
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.assign(`/authn/login?next=${next}`);
            return new Promise<AxiosResponse<BaseSchemas.CustomerAppConfig>>(() => {});
          }
          // On the email-constraint page the backend returns a 400 for this user (and the public app config would too,
          // since the token is valid but the user can't be resolved). Return a fallback config so the page can render
          // rather than throwing into the generic error boundary.
          if (isAuthPath && e.status === 400) {
            return {
              ...e.response,
              data: EMAIL_CONSTRAINT_FALLBACK_APP_CONFIG,
            } as AxiosResponse<BaseSchemas.CustomerAppConfig>;
          }
        }
        throw e;
      }
    },
    select: ({ data }) => data,
    refetchOnMount: !isAuthPath,
    staleTime: isAuthPath ? 60 * 1000 : 0,
    ...options,
  });
};
